import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import prepareAuthHeaders from "@utils/prepareAuthHeaders";
import { Character } from "@models/character";
import { PageResponse } from "@models/page-response";
import { personApi } from "@store/personApi";
import { accountSetupApi } from "@store/accountSetupApi";

const BASE_API_URL = process.env.REACT_APP_API_URL;

export interface CharacterUpdate {
  id: string;
  personId: string | null;
  merge?: boolean;
}

export interface GetCharactersQuery {
  patientId: string;
  photoId?: string;
  search?: string;
  includeFaces?: boolean;
}

export const characterApi = createApi({
  reducerPath: "characterApi",
  tagTypes: ["Character"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_API_URL}/characters`,
    prepareHeaders: prepareAuthHeaders,
  }),
  endpoints: (builder) => ({
    getCharacters: builder.query<PageResponse<Character>, GetCharactersQuery>({
      providesTags: ["Character"],
      query: (params: GetCharactersQuery) => {
        return {
          url: ``,
          method: "GET",
          params,
        };
      },
    }),
    updateCharacter: builder.mutation<Character, CharacterUpdate>({
      invalidatesTags: ["Character"],
      query: ({ id, personId, merge }: CharacterUpdate) => {
        return {
          url: id,
          method: "PATCH",
          body: {
            personId,
            merge,
          },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(personApi.util.invalidateTags(["Person"]));
        dispatch(accountSetupApi.util.invalidateTags(["AccountSetupProgress"]));
      },
    }),
    deleteCharacter: builder.mutation<void, string>({
      invalidatesTags: ["Character"],
      query: (id: string) => {
        return {
          url: id,
          method: "DELETE",
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(personApi.util.invalidateTags(["Person"]));
        dispatch(accountSetupApi.util.invalidateTags(["AccountSetupProgress"]));
      },
    }),
  }),
});

export const {
  useGetCharactersQuery,
  useUpdateCharacterMutation,
  useDeleteCharacterMutation,
} = characterApi;
